import { bindPage } from '../modules/page_lifecycle.js';
import { fetchWithCsrf } from '../modules/csrf.js';
import { bootstrapModal, showModal } from '../modules/bootstrap_ui.js';
import { createChartHelper } from '../modules/chart_helper.js';
import { AnalysisDateSlider } from '../modules/analysis_date_slider.js';
import { AnalysisAccordion } from '../modules/analysis_accordion.js';
import { isEmpty, mergeObjects } from '../modules/object_utils.js';

const CHART_OPTIONS = {
  line: {
    chart: { type: 'line' },
    title: { text: 'Monthly Average Temperature' },
    xAxis: { categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] },
    yAxis: { title: { text: 'Temperature (°C)' } },
    plotOptions: {
      line: { dataLabels: { enabled: true }, enableMouseTracking: false }
    },
    series: [
      { name: 'Tokyo', data: [7.0, 6.9, 9.5, 14.5, 18.4, 21.5, 25.2, 26.5, 23.3, 18.3, 13.9, 9.6] },
      { name: 'London', data: [3.9, 4.2, 5.7, 8.5, 11.9, 15.2, 17.0, 16.6, 14.2, 10.3, 6.6, 4.8] }
    ]
  },
  column_stack: {
    chart: { type: 'column' },
    title: { text: 'Stacked column chart' },
    xAxis: { categories: ['Apples', 'Oranges', 'Pears', 'Grapes', 'Bananas'] },
    yAxis: {
      min: 0,
      title: { text: 'Total fruit consumption' },
      stackLabels: { enabled: true, style: { fontWeight: 'bold', color: 'gray' } }
    },
    plotOptions: {
      column: {
        stacking: 'normal',
        dataLabels: { enabled: false, color: 'white' }
      }
    },
    series: [
      { name: 'John', data: [5, 3, 4, 7, 2] },
      { name: 'Jane', data: [2, 2, 3, 2, 1] },
      { name: 'Joe', data: [3, 4, 4, 2, 5] }
    ]
  },
  pie: {
    chart: { plotBackgroundColor: null, plotBorderWidth: null, plotShadow: false },
    title: { text: 'Browser market shares at a specific website, 2010' },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: 'pointer',
        dataLabels: {
          enabled: true,
          color: '#000000',
          connectorColor: '#000000',
          formatter() {
            return `<b>${this.point.name}</b>: ${this.percentage} %`;
          }
        }
      }
    },
    series: [{
      type: 'pie',
      name: 'Browser share',
      data: [
        ['Firefox', 45.0],
        ['IE', 26.8],
        { name: 'Chrome', y: 12.8, sliced: true, selected: true },
        ['Safari', 8.5],
        ['Opera', 6.2],
        ['Others', 0.7]
      ]
    }]
  },
  column: {
    chart: { type: 'column' },
    title: { text: 'Monthly Average Rainfall' },
    xAxis: {
      categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    },
    yAxis: { min: 0, title: { text: 'Rainfall (mm)' } },
    series: [
      { name: 'Tokyo', data: [49.9, 71.5, 106.4, 129.2, 144.0, 176.0, 135.6, 148.5, 216.4, 194.1, 95.6, 54.4] },
      { name: 'New York', data: [83.6, 78.8, 98.5, 93.4, 106.0, 84.5, 105.0, 104.3, 91.2, 83.5, 106.6, 92.3] },
      { name: 'London', data: [48.9, 38.8, 39.3, 41.4, 47.0, 48.3, 59.0, 59.6, 52.4, 65.2, 59.3, 51.2] },
      { name: 'Berlin', data: [42.4, 33.2, 34.5, 39.7, 52.6, 75.5, 57.4, 60.4, 47.6, 39.1, 46.8, 51.1] },
      { name: 'Manila', data: [42.4, 33.2, 34.5, 39.7, 52.6, 75.5, 300.4, 400.4, 250.6, 39.1, 46.8, 51.1] }
    ]
  }
};

function bindAnalysisPage() {
  const accordionRoot = document.getElementById('accordion');
  if (!accordionRoot || typeof Highcharts === 'undefined') return;

  document.querySelector('.main')?.classList.add('clearfix');

  const dataSets = {};
  window.__analysisDataSets = dataSets;
  let chartInstance = null;
  let tabCount = 1;

  const helper = createChartHelper({
    dataSets,
    getChart: () => chartInstance,
    setChart: (chart) => {
      chartInstance = chart;
    }
  });

  globalThis.helper = helper;

  const formTemplateHtml = document.getElementById('dataSetForm')?.closest('.formContainer')?.innerHTML || '';
  const dateRangeContainer = document.getElementById('dateRangeContainer');
  const dateSlider = new AnalysisDateSlider({
    minInput: document.getElementById('dateSliderMin'),
    maxInput: document.getElementById('dateSliderMax'),
    display: document.getElementById('dateRange'),
    container: dateRangeContainer,
    onStop: () => document.getElementById('button_redraw')?.click()
  });

  helper.setup_help_pane();
  bootstrapModal('projectsViewerModal');

  const accordion = new AnalysisAccordion(accordionRoot, {
    formTemplateHtml,
    dataSets,
    helper
  });

  const redrawChart = async () => {
    const form = document.getElementById('dataSetForm');
    const panel = form?.closest('.ui-accordion-content');
    const header = panel?.previousElementSibling;
    const key = helper.parse_dataset_id(header?.id);

    dataSets[key] = dataSets[key] || {};
    new FormData(form).forEach((value, name) => {
      dataSets[key][name] = value;
    });

    if (!dateSlider.init) dateSlider.initialize().setValues(helper.get_settings.bind(helper), dataSets);

    const range = dateSlider.getValues();

    try {
      const body = new FormData();
      body.append('filters', JSON.stringify(dataSets));
      body.append('settings', JSON.stringify(helper.get_settings()));
      range.forEach((year) => body.append('date_range[]', year));

      const response = await fetchWithCsrf('/analysis/get_data', {
        method: 'POST',
        body,
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) return;

      const data = await response.json();
      const chartType = helper.get_settings().type;

      helper.setup(data.categories, data.data);
      const newOptions = helper.build_options();
      newOptions.lang = {
        noData: dateSlider.isVisible() ? 'No data within the specified range' : 'No data to display'
      };
      if (data.categories.length > 21) {
        newOptions.xAxis.labels.step = Math.floor((data.categories.length / 10) / 2);
      }

      chartInstance?.destroy();
      chartInstance = Highcharts.chart('container', mergeObjects(CHART_OPTIONS[chartType], newOptions));
      dateSlider.setEnabled(true);
    } catch (error) {
      console.log(error);
    }
  };

  const onSubmit = (event) => {
    if (event.target.id === 'dataSetForm') {
      event.preventDefault();
      document.getElementById('button_redraw')?.click();
    }
  };

  const onClick = (event) => {
    if (event.target.closest('#button_reset_form')) {
      event.preventDefault();
      const form = event.target.closest('form');
      form?.reset();
      const display = document.getElementById('companyNameDisplay');
      if (display) display.innerHTML = '';
      document.querySelectorAll('#company_id, #company_name').forEach((field) => field.removeAttribute('value'));
      return;
    }

    if (event.target.closest('#button_view_plants')) {
      event.preventDefault();
      const form = event.target.closest('form');
      if (!form) return;
      const params = new URLSearchParams(new FormData(form));
      params.set('page', '1');
      const iframe = document.getElementById('projectsViewerFrame');
      if (iframe) iframe.src = `/projects/project_table?${params.toString()}`;
      showModal('projectsViewerModal');
      return;
    }

    if (event.target.closest('#button_remove_dataset')) {
      event.preventDefault();
      const panel = event.target.closest('.ui-accordion-content');
      if (panel) accordion.markPanelForRemoval(panel.id);
      return;
    }

    const toggle = event.target.closest('.toggle_ds_name');
    if (toggle) {
      toggle.classList.toggle('ds_name_show');
      const panel = toggle.parentElement?.querySelector('.dataset_div');
      if (panel) panel.style.display = toggle.classList.contains('ds_name_show') ? '' : 'none';
    }
  };

  document.addEventListener('submit', onSubmit);
  document.addEventListener('click', onClick);

  document.querySelector('.chart_download_pdf')?.addEventListener('click', () => {
    chartInstance?.exportChart({ type: 'application/pdf', filename: 'DesalData' });
  });

  document.querySelector('.chart_download_png')?.addEventListener('click', () => {
    chartInstance?.exportChart({ type: 'application/png', filename: 'DesalData' });
  });

  document.querySelector('.chart_download_csv')?.addEventListener('click', () => {
    const form = document.getElementById('chart_download_csv');
    form?.querySelector('#chart_data')?.setAttribute('value', JSON.stringify(helper.data));
    form?.querySelector('#categories')?.setAttribute('value', JSON.stringify(helper.categories));
    form?.querySelector('#datasets')?.setAttribute('value', JSON.stringify(dataSets));
    form?.querySelector('#y_axis')?.setAttribute('value', helper.get_settings().y);
    form?.querySelector('#submit_csv_form')?.click();
  });

  document.getElementById('button_redraw')?.addEventListener('click', redrawChart);

  document.getElementById('button_add_dataset')?.addEventListener('click', () => {
    const headerCount = accordionRoot.querySelectorAll('.ui-accordion-header').length;
    if (headerCount < 7) {
      tabCount += 1;
      accordion.addPanel(tabCount);
    }
  });

  document.querySelectorAll('#chart_settings_content a, .chart_type a, #chart_y a, #chart_x a').forEach((link) => {
    link.addEventListener('click', (event) => {
      event.preventDefault();
      const list = link.closest('ul');
      list?.querySelectorAll('a').forEach((item) => item.classList.remove('selected'));
      link.classList.add('selected');
    });
  });

  document.querySelectorAll('#chart_settings_content a').forEach((link) => {
    link.addEventListener('click', () => chartInstance?.showLoading());
  });

  document.getElementById('cumulative_data')?.addEventListener('change', () => {
    document.getElementById('button_redraw')?.click();
  });

  const rebuildChartFromSettings = () => {
    if (helper.categories === undefined) {
      document.getElementById('button_redraw')?.click();
      return;
    }
    chartInstance?.destroy();
    chartInstance = Highcharts.chart(
      'container',
      mergeObjects(CHART_OPTIONS[helper.get_settings().type], helper.build_options())
    );
  };

  document.querySelectorAll('.chart_type a, #chart_y a').forEach((link) => {
    link.addEventListener('click', rebuildChartFromSettings);
  });

  document.querySelectorAll('#chart_x a').forEach((link) => {
    link.addEventListener('click', () => {
      dateSlider.setValues(helper.get_settings.bind(helper), dataSets);
      document.getElementById('button_redraw')?.click();
    });
  });

  const updateCumulativeVisibility = () => {
    const settings = helper.get_settings();
    const displayCumulative = {
      column: ['award_date', 'online_date'],
      column_stack: ['award_date', 'online_date'],
      line: ['award_date', 'online_date'],
      pie: []
    };
    const cumulativeContainer = document.getElementById('cumulativeContainer');
    const cumulativeInput = document.getElementById('cumulative_data');
    if (displayCumulative[settings.type]?.includes(settings.x)) {
      cumulativeContainer?.style.setProperty('display', '');
    } else {
      if (cumulativeInput) cumulativeInput.checked = false;
      cumulativeContainer?.style.setProperty('display', 'none');
    }
  };

  document.querySelectorAll('.chart_type a, #chart_x a, #chart_y a').forEach((link) => {
    link.addEventListener('click', updateCumulativeVisibility);
  });

  document.querySelector('.toggle_chart_title')?.addEventListener('click', (event) => {
    event.currentTarget.classList.toggle('chart_title_show');
    const panel = event.currentTarget.closest('.title_div')?.querySelector('.title_input_div');
    if (!panel) return;
    panel.style.display = event.currentTarget.classList.contains('chart_title_show') ? '' : 'none';
  });

  document.getElementById('chart_title')?.addEventListener('keyup', (event) => {
    if (!chartInstance) return;
    const value = event.currentTarget.value.trim();
    if (value !== '') {
      chartInstance.setTitle({ text: value });
    } else {
      chartInstance.setTitle({ text: helper.build_chart_title(helper.get_settings()).title });
    }
  });

  const onChange = (event) => {
    if (event.target.closest('#dataSetForm')) {
      const form = event.target.closest('#dataSetForm');
      const serializedForm = {};
      new FormData(form).forEach((value, key) => {
        serializedForm[key] = value;
      });
      const panelId = form.parentElement?.id;
      dataSets[helper.parse_dataset_id(panelId)] = serializedForm;
    }

    if (event.target.matches('#date_range_start, #date_range_end')) {
      const panel = event.target.closest('.ui-accordion-content');
      const dsId = helper.parse_dataset_id(panel?.id);
      dataSets[dsId] = dataSets[dsId] || {};
      dataSets[dsId][event.target.id] = event.target.value;
      dateSlider.setValues(helper.get_settings.bind(helper), dataSets);
    }
  };

  const onInput = (event) => {
    if (event.target.matches('#dataset_name')) {
      const form = event.target.closest('form');
      const panelId = form?.parentElement?.id;
      const header = document.getElementById(panelId?.replace('panel', 'header'));
      const link = header?.querySelector('a');
      if (!link) return;

      if (!isEmpty(event.target.value)) {
        link.textContent = event.target.value.substring(0, 20);
      } else {
        link.textContent = `Data set ${header.id.match(/[0-9]/)?.join('') || '1'}`;
      }
    }
  };

  document.addEventListener('change', onChange);
  document.addEventListener('input', onInput);

  updateCumulativeVisibility();
  document.getElementById('button_redraw')?.click();

  return () => {
    chartInstance?.destroy();
    chartInstance = null;
    document.removeEventListener('submit', onSubmit);
    document.removeEventListener('click', onClick);
    document.removeEventListener('change', onChange);
    document.removeEventListener('input', onInput);
  };
}

bindPage(bindAnalysisPage);
